<template>
  <div class="container">
    <div class="frame">
      <div class="frame__inner">
        <div class="frame__header">
          <Back
            :title="$t('replenish_the_balance')"
            :redirect="redirect"
          />
        </div>
        <div class="frame__body">
          <div class="sim-remittances">
            <div class="sim-remittances__inner">
              <!-- phone -->
              <div class="sim-remittances__phone">
                <ap-input-tel
                  id="receiver"
                  v-model="payee.recipient"
                  :label="$t('sim_remittances.recipient')"
                  :icon-name="receiverOperator"
                  disabled
                  @input="keyup"
                />
              </div>
              <!-- sum -->
              <div class="sim-remittances__sum">
                <ap-input-money
                  v-model="payee.sum"
                  :label="$t('amount')"
                  currency
                  :error-text="refillError.amount"
                  @keyup="changeSum"
                />
              </div>
              <div>
                <!-- payment -->
                <div class="sim-remittances__payment">
                  <div class="payment">
                    <div class="payment__inner">
                      <div class="payment__header">
                        <h3 class="payment__title">
                          {{ $t('from') }}
                        </h3>
                        <div class="payment__picker">
                          <!-- card -->
                          <ap-radio 
                            v-if="cardPaymentEnable"
                            id="card"
                            :text="$t('bank_card')"
                            :checked="currentPaymentMethodCard"
                            icon-name="paymentCard.svg"
                            v-on="cardPaymentEnable && simPaymentEnable ? { click: switchPayToCard } : {}"
                          />
                          <!-- phone -->
                          <div class="payment__card">
                            <ap-radio
                              v-if="simPaymentEnable"
                              id="sim "
                              :text="$t('mobile_balance')"
                              :checked="!currentPaymentMethodCard"
                              icon-name="paymentSim.svg"
                              v-on="simPaymentEnable  && cardPaymentEnable ? { click: switchPaymentToSim } : {}"
                            />
                          </div>
                        </div>
                      </div>
                      <div class="payment__body">
                        <!-- card -->
                        <div
                          v-if="currentPaymentMethod === CARD"
                          class="payment__card-form"
                        >
                          <div :class="['card-form', JsSDKError.class]">
                            <div class="card-form__item" >
                              <ap-input-mask  
                                v-model="cardDetails.number"
                                type="cardNumber"
                                full
                                border
                              />
                            </div>
                            <div class="card-form__item">
                              <ap-input-mask
                                v-model="cardDetails.expDate"
                                border
                                type="expireDate"
                              />
                            </div>
                            <div class="card-form__item">
                              <ap-input
                                v-model="cardDetails.cvv"
                                border
                                type="cvv"
                                length="3"
                                placeholder="CVV/CVC"
                              />
                            </div>
                            <div class="card-form__item">
                              <ap-input
                                v-model="cardDetails.holderName"
                                border
                                :placeholder="$t('holder_name')"
                              />
                            </div>
                          </div>
                          <div
                            v-if="JsSDKError"
                            class="js-sdk-error"
                          >
                            {{ JsSDKError.text }}
                          </div>
                        </div>
                        <!-- phone -->
                        <div
                          v-if="currentPaymentMethod === SIM"
                          class="payment__phone"
                        >
                          <ap-input-tel
                            id="sender"
                            v-model="payee.phone"
                            :label="t('sim_remittances.sim_to_sim_label')"
                            :icon-name="senderOperator"
                            :error-text="sim2simErrorText"
                            @keyup="keyup"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- receipt -->
                <!-- total -->
                <div class="sim-remittances__total">
                  <div class="total">
                    <div class="total__item">
                      <div class="total__text">
                        {{ $t('commission') }}
                      </div>
                      <div class="total__number">
                        {{ payee.commission || 0 }} ₸
                      </div>
                    </div>
                    <div class="total__item">
                      <div class="total__text">
                        {{ $t('write_off_amount') }}
                      </div>
                      <div class="total__number">
                        {{ formatNumber(payee.total) || 0 }} ₸
                      </div>
                    </div>
                  </div>
                </div>
                <!-- btn -->
                <div class="sim-remittances__btn">
                  <ap-button
                    type="primary"
                    full
                    :disable="disabledBtn"
                    @click="pay"
                  >
                    <div class="button__loader">
                      <div
                        v-if="checking"
                        class="spinner"
                      />
                      <div v-else>
                        {{ $t('replenish') }}
                        <span v-if="payee.amount">{{ formatNumber(payee.amount) }} ₸</span>
                      </div>
                    </div>
                  </ap-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">



import { computed, defineComponent, onBeforeMount, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { 
  CARD, 
  PHONE_LENGTH,
  REMITTAMCES_TO_SIM, 
  SIM, 
  STATUS_CONFIRM,
  STATUS_ERROR,
  STATUS_OK,
  STATUS_SUCCESS
} from '@/constant';

import { 
  decodeBase64,
  formatNumber 
} from '@/util/helper';

import apButton from '@/components/UI/apButton.vue';
import apInput from '@/components/UI/apInput.vue';
import apInputMask from '@/components/UI/apInputMask.vue';
import apInputMoney from '@/components/UI/apInputMoney.vue';
import apInputTel from '@/components/UI/apInputTel.vue';
import apLoader from '@/components/UI/apLoader.vue';
import apRadio from '@/components/UI/apRadio.vue';
import Back from '@/components/Back.vue';

export default defineComponent({
  name: 'SimRemittances',
  
  components: {
    apInputTel,
    apInputMask,
    apButton,
    apInput,
    Back,
    apInputMoney,
    apLoader,
    apRadio
  },

  setup() {
    const store = useStore();
    const router = useRouter();
    const { t } = useI18n();

    // придумать новое
    const currentPaymentMethod = ref('card');
    const checking = ref(false);
    const cardPaymentEnable = ref(true);
    const simPaymentEnable = ref(true);
    const isValidPayeeDetails = ref(false);

    const payee = ref({
      recipient: '',
      amount: '',
      commission: '',
      total: '',
      receipt: '',
      sum: '',
      phone: '',
      maxAmount: ''
    });

    const redirect = ref({
      name: 'main',
      text: t('main')
    });

    const cardDetails = ref({
      number: '',
      cvv: '',
      expDate: '',
      holderName: ''
    });

    const JsSDKError = ref({
      text: '',
      class: ''
    });


    const language = computed(() => store.getters['language']);
    const remittancesFromCard = computed(() => store.getters['simRemittances/remittancesFromCard']);
    const remittancesFromSim = computed(() => store.getters['simRemittances/remittancesFromSim']);
    const remittancesFromSimErrors = computed(() => store.getters['simRemittances/remittancesFromSimErrors']);
    const remittancesFromCardErrorsCard = computed(() => store.getters['simRemittances/remittancesFromCardErrorsCard']);
    const remittancesFromSimErrorsSim = computed(() => store.getters['simRemittances/remittancesFromSimErrorsSim']);
    const loading = computed(() => store.getters['loading']);
    const sim2simError = computed(() => store.getters['simRemittances/sim2simError']);
    const currentSum = computed(() => remittancesFromCard.value.amount || remittancesFromCard.value.amount);
    const sumHint = computed(() =>  `${t('maximum_amount_to_pay')} ${payee.value.maxAmount} ₸`); 
    const currentPaymentMethodCard = computed(() => currentPaymentMethod.value === CARD);

    const sim2simErrorText = computed(() => 
    {
      let errorText = '';
      if (sim2simError.value && sim2simError.value.errors) {
        errorText = sim2simError.value && sim2simError.value.errors ? sim2simError.value.message : '';
      }
      if (remittancesFromSimErrors.value && remittancesFromSimErrors.value.errors ) {
        errorText = remittancesFromSimErrors.value && remittancesFromSimErrors.value.errors ? remittancesFromSimErrors.value.message : '';
      }
      return errorText;
    });  

    const refillError = computed(() => {
      const errors = {
        amount: ''
      };
      const { errors: cardError } = remittancesFromCardErrorsCard.value;
      const { errors: simError } = remittancesFromSimErrorsSim.value;
      if (cardError && simError) {
        errors.amount = cardError?.amount?.[0]  || simError?.amount?.[0];
      }
      return errors;
    });
    
    const senderOperator = computed(() => {
      const { operator } = store.getters['senderOperator'];
      return operator || '';
    });

    const receiverOperator = computed(() => {
      const { operator } = store.getters['receiverOperator'];
      return operator || '';
    });

    const receiptOperator = computed(() => {
      const { operator } = store.getters['receiptOperator'];
      return operator?.code || '';
    });

    const changeSum = async (): Promise<void> => {
      const amount = payee.value.sum.replaceAll(/\s/g, '').replace(/\D/g, '');
  
      
      if (refillError.value.amount.length) {
        store.commit('simRemittances/setRemittancesFromCardErrorsCard', {});
        store.commit('simRemittances/setRemittancesFromSimErrorsSim', {});
      }
      
      if (!isNaN(+payee.value.sum) && amount !== currentSum.value && !refillError.value.amount.length) {
        store.commit('setLoading', true);
        store.commit('simRemittances/setInfoRemittancesFromSim', {});
        store.commit('simRemittances/setInfoRemittancesFromCard', {});
        
        const data = {
          phone: '7' + payee.value.recipient,
          amount,
          language: language.value,
          flag: CARD
        };
        
        const infoFromCard = await store.dispatch('simRemittances/prevalidateRemittancesFromCard', data);
        const infoFromSim = await store.dispatch('simRemittances/prevalidateRemittancesFromSim', {
          description: 'Перевод с баланса на баланс',
          type: SIM,
          amount,
          recipient: '7' + payee.value.recipient,
          language: language.value,
          flag: SIM
        });

        const isPossiblePayment = (infoFromCard.status && infoFromCard.status === STATUS_OK) 
                                || (infoFromSim.status && infoFromSim.status === STATUS_OK); 

        if (isPossiblePayment) {
          composeData();
        }
        store.commit('setLoading', false);
      } 
    };

    const updateComission = (): void => {
      payee.value = { 
        ...payee.value,
        commission: remittancesFromSim.value.commission.amount,
        total: remittancesFromSim.value.commission.total,
      };
    };

    const composeData = (): void => {
      if (Object.keys(remittancesFromSim.value).length && !Object.keys(remittancesFromCard.value).length) {
        payee.value = { 
          ...remittancesFromSim.value,
          commission: remittancesFromSim.value.commission.amount,
          total: +remittancesFromSim.value.commission.total,
          sum: remittancesFromSim.value.amount,
          maxAmount: remittancesFromSim.value.max_amount,
          recipient: remittancesFromSim.value.recipient.substring(1) || '',
          phone: '',
          receipt: ''
        };
        currentPaymentMethod.value = SIM;
        cardPaymentEnable.value = false;
        simPaymentEnable.value =true;
      } else if (!Object.keys(remittancesFromSim.value).length && Object.keys(remittancesFromCard.value).length) {
        payee.value = { 
          ...remittancesFromCard.value,
          recipient: remittancesFromCard.value.phone ? remittancesFromCard.value.phone.substring(1) : remittancesFromCard.value.recipient.substring(1),
          commission: remittancesFromCard.value.commission.amount,
          total: remittancesFromCard.value.commission.total,
          maxAmount: remittancesFromCard.value.max_amount,
          sum: remittancesFromCard.value.amount,
          receipt: ''
        };
        currentPaymentMethod.value = CARD;
        cardPaymentEnable.value = true;
        simPaymentEnable.value = false;
      } 
      else {
        payee.value = { 
          ...remittancesFromCard.value,
          recipient: remittancesFromCard.value.phone ? remittancesFromCard.value.phone.substring(1) : remittancesFromCard.value.recipient.substring(1),
          commission: remittancesFromCard.value.commission.amount,
          total: +remittancesFromCard.value.commission.total,
          sum: remittancesFromCard.value.amount,
          maxAmount: remittancesFromCard.value.max_amount,
          phone: '',
          receipt: ''
        };
        currentPaymentMethod.value = CARD;
        cardPaymentEnable.value = true;
        simPaymentEnable.value = true;
      }
    };

    const switchPaymentToSim = (): void => {
      const isPossiblePayment = Object.keys(remittancesFromSim.value).length || Object.keys(remittancesFromCard.value).length;
      
      if (isPossiblePayment && cardPaymentEnable.value && simPaymentEnable) {
        
        JsSDKError.value = {
          text: '',
          class: ''
        };

        payee.value.receipt = '';
        currentPaymentMethod.value = SIM;
        payee.value.commission = remittancesFromSim.value.commission.amount;
        payee.value.total = formatNumber(remittancesFromSim.value.commission.total);          
      }
    };
    const switchPayToCard = ():void => {
      const isPossiblePayment = Object.keys(remittancesFromSim.value).length || Object.keys(remittancesFromCard.value).length;

      if (isPossiblePayment && cardPaymentEnable.value && simPaymentEnable) {
        currentPaymentMethod.value = CARD;
        payee.value.phone = '';
        payee.value.commission = remittancesFromCard.value.commission.amount;
        payee.value.total = formatNumber(remittancesFromCard.value.commission.total);
        store.commit('setOperator', {type: 'sender'});
      }
    };

    const disabledBtn = computed(() => {
      return !(
        (!refillError.value.amount.length 
        && ((payee.value.phone.length === PHONE_LENGTH) 
        || (cardDetails.value.cvv.length === 3 
        && cardDetails.value.number.length === 16 
        && cardDetails.value.expDate.length === 5 
        && cardDetails.value.holderName.length > 0)) )
       
      );
    });


    const sdkErrors = (error: string) => {
      let text = '';
      if (error) {
        if (error.includes('pg card name')) {
          return {
            text: 'Поле "Имя владельца" введено не корректно',
            class: 'card-form-name-error'
          };
        }
        else if (error.includes('pg card pan')) {
          return {
            text: 'Поле "Номер карты" введено не корректно',
            class: 'card-form-card_number-error'
          };
        }
        else if (error.includes('pg card month')) {
          return {
            text: 'Поле "MM/YY" введено не корректно.',
            class: 'card-form-exp_date-error'
          };    
        } else {
          return {
            text: error,
            class: ''
          };   
        }
      }
      return { 
        text,
        class: ''
      };
    };

    const keyup = async (e:Event) => {
      const element = e.target as HTMLInputElement;
      const cureentTarget = e.currentTarget as HTMLInputElement;

      const text = element.value.replace(/\s/g, '');
      text.length === PHONE_LENGTH
        ?  await store.dispatch('checkOperator', 
          { 
            phone: `7${text}`,
            type: cureentTarget.id 
          })
        : store.commit('setOperator', { type: cureentTarget.id });
      
      if (payee.value.phone && payee.value.phone.length === PHONE_LENGTH) {
        const infoFromSim = await store.dispatch('simRemittances/prevalidateRemittancesFromSim', {
          description: 'Перевод с баланса на баланс',
          type: SIM,
          amount: payee.value.sum,
          recipient:'7' + payee.value.recipient,
          phone: '7' + payee.value.phone,
          language: language.value,
          flag: SIM 
        });
        if (infoFromSim && infoFromSim.status === STATUS_OK) {
          updateComission();
        }
      }
      


      if (sim2simError.value && sim2simError.value.errors){ 
        store.commit('simRemittances/setSim2simError', {});
      } 

      if (remittancesFromSimErrors.value && remittancesFromSimErrors.value.errors){ 
        store.commit('simRemittances/setRemittancesFromSimErrors', {});
      } 
    };

    const checkPayeeDetails = () => {
      const data = {
        phone: '7' + payee.value.recipient,
        amount: payee.value.amount
      };
      store.dispatch('simRemittances/prevalidateRemittances', data);
    };

    const composeCardDetails = () => {
      const init = {
        phone: '7' + payee.value.recipient,
        amount: +payee.value.sum,
        testmode: 0,
        language: language.value,
        email: 'example@domain.com',
      };

      const options = {
        card_number: cardDetails.value.number.replace(/\s+/g, ''),
        card_holder_name: cardDetails.value.holderName,
        card_exp_month: cardDetails.value.expDate.substring(0,2),
        card_exp_year: cardDetails.value.expDate.slice(-2),
        card_cvv: cardDetails.value.cvv.toString(),
      };

      const JSTransactionOptionsBankCard = {
        type: 'bank_card',
        options: options
      };

      const JSPaymentOptions = {
        auto_clearing: 1, 
        amount: +payee.value.total, 
        currency: 'KZT', 
        description: 'Описание заказа',
        order_id: '',
        test: 0,
        
        options: {
          custom_params: {},
          user: {
            email: 'string',
            phone: 'string'
          }
        },
      };


      return { init,
        JSTransactionOptionsBankCard,
        JSPaymentOptions };
    };

    const composeSimDetails = () => {
      const init = {
        phone: '7' + payee.value.phone,
        amount: +payee.value.amount,
        type: SIM,
        recipient:  '7' + payee.value.recipient,
        description: 'Transfer from balance to balance',
        language: language.value
      };
      
      return { init };
    };

    const confirmRemittances = async (paymentData) => await store.dispatch('simRemittances/confirmRemittances', paymentData);

    const paymentSDK = async (JSPaymentOptions, JSTransactionOptionsBankCard, transactionId) => {
      
      let JSPayResult = {
        payment_status: '',
        order_id: '',
        payment_id: '',
        status: '',
        error_message: ''
      };
      const wd: Window = window;
      try {

        JSPayResult = await wd.FreedomPaySDK.charge(
          JSPaymentOptions, JSTransactionOptionsBankCard
        );

        if (JSPayResult.payment_status === STATUS_CONFIRM) {
          store.commit('setJsSDK', 
            {
              ...JSPayResult,
              transactionId
            });
          
          router.push({
            name: 'iframe',
            params: {llid: '3dsForm'}
          });
        }

        if (JSPayResult.status === STATUS_ERROR) {
          const { error_message } = JSPayResult;
          JsSDKError.value = sdkErrors(error_message);
        }
      } catch (JSErrorObject) {

        const { response } = JSErrorObject;
        JsSDKError.value = sdkErrors(response.error_message);
      
      }
      return JSPayResult;
    };

    const initializationPayment = async (paymenData) => {
      const wd: Window = window;
      return await store.dispatch('simRemittances/initializationRemittances', paymenData)
        .then((res) => {
          if (res && res.code === 1000) {
            const key = decodeBase64(res.sdk.key);
            wd.FreedomPaySDK.setup(key, res.sdk.token);
            return res;
          }
        }).catch((err) => {
          return err.response;
        });
    };
    
    const pay = async () => {
      checking.value = true;
      if (currentPaymentMethod.value === CARD) {
        const { init: data, JSPaymentOptions, JSTransactionOptionsBankCard } = composeCardDetails();
        
        const initialization = await initializationPayment(data);
        
        if (initialization && initialization.status === STATUS_OK){

          
            
                    
          JSPaymentOptions.order_id = initialization.order_id;
          
          const paymentResult = await paymentSDK(JSPaymentOptions, JSTransactionOptionsBankCard, initialization.transaction_id );
          if (paymentResult && paymentResult.payment_status === STATUS_SUCCESS) {
            const init = {
              transaction_id: initialization.transaction_id,
              order_id: initialization.order_id,
              payment_id: paymentResult.payment_id
            };
          
            const confirm = await confirmRemittances(init);

            if (confirm && (confirm.status === STATUS_OK || confirm.status === STATUS_SUCCESS)) {

              router.push(
                { name: 'service-status',
                  params: { 
                    phone: payee.value.recipient,
                    payment_id: confirm.payment.id,
                    paymentType: REMITTAMCES_TO_SIM
                  } 
                });
            }
          }
        }
      }
      
      if (currentPaymentMethod.value === SIM) {
        const { init: data } = composeSimDetails();

        const info = await store.dispatch('simRemittances/prevalidateRemittancesFromSim', {
          ...data
        });

  
        if (info.status === STATUS_OK) {
          
          const { data: sim2sim } = await store.dispatch('simRemittances/sim2sim', data);
          
          if (sim2sim.confirm_inputs && sim2sim.confirm_inputs.length) {

            await store.dispatch('simRemittances/resendOtp', 
              { 
                id: sim2sim.payment.id,
                language: language.value
              });

            store.commit('setOtp', {
              phone: payee.value.phone,
              service_id: sim2sim.payment.id,
              paymentType: REMITTAMCES_TO_SIM,
              otp: ''
            });

            router.push(
              { name: 'confirm',
                params: { 
                  phone: payee.value.phone,
                  service_id: sim2sim.payment.id,
                  paymentType: REMITTAMCES_TO_SIM,
                  otp: ''
                } 
              });
            return;
          }
        
          if (sim2sim.errors && sim2sim.errors.phone && sim2sim.errors.phone[0]) {
            checking.value = false;
            return;
          }

          if ((sim2sim.errors && sim2sim.errors?.type)  || (sim2sim.status === STATUS_ERROR && sim2sim.code === 2012)) {

            router.push(
              { name: 'service-status',
                params: { 
                  phone: payee.value.recipient,
                  status: STATUS_ERROR,
                  failure_description: sim2sim.message|| sim2sim.errors?.type?.[0] || '',
                  paymentType: REMITTAMCES_TO_SIM
                } 
              });
            return;
          }

          router.push(
            { name: 'service-status',
              params: { 
                phone: payee.value.recipient,
                payment_id: sim2sim.payment.id,
                paymentType: REMITTAMCES_TO_SIM
              } 
            });
        }
        if (info.errors && info.errors.phone && info.errors.phone[0]) {
          checking.value = false;
          return;
        }
      }
      checking.value = false;
      return; 
    };

    onBeforeMount(() => {
      composeData();
    });

    onBeforeUnmount(() => {
      store.commit('clearOperator');
      store.commit('simRemittances/setRemittancesFromCardErrors', {});
      store.commit('simRemittances/setRemittancesFromSimErrors', {});
      store.commit('simRemittances/setInfoRemittancesFromSim', {});
      store.commit('simRemittances/setInfoRemittancesFromCard', {});
      store.commit('simRemittances/setSim2simError', {});
    });
    
    return {
      payee,
      cardDetails,
      isValidPayeeDetails,
      pay,
      checkPayeeDetails,
      currentPaymentMethod,
      switchPaymentToSim,
      redirect,
      keyup,
      senderOperator,
      receiverOperator,
      receiptOperator,
      JsSDKError,
      checking,
      SIM,
      CARD,
      disabledBtn,
      formatNumber,
      currentPaymentMethodCard,
      sim2simErrorText,
      cardPaymentEnable,
      simPaymentEnable,
      changeSum,
      refillError,
      loading,
      sumHint,
      switchPayToCard,
      t
    };
  },
});
</script>
<style lang="scss" scoped>
.sim-remittances{
  padding: 34px 16px 48px 16px;
  &__inner{
    max-width: 350px;
    width: 100%;
    margin: 0 auto;
  }
  &__phone{
    margin-bottom: 18px;
  }
  &__sum{
    margin-bottom: 40px;
  }
  &__receipt{
    margin-bottom: 24px;
  }
  &__payment{
    margin-bottom: 24px;
  }
  &__total{
    margin-bottom: 40px;
  }
  &__loader{
    height: 200px;
  }
  &__btn{
    *{
      color: #FFFFFF;
    }
  }
}
.card-form {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}
.card-form__item:nth-child(1) {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border: 1px solid rgba(176, 183, 195, 0.6);
  grid-column: 1 /3;
}
.card-form__item:nth-child(2) {
  border-right: 1px solid rgba(176, 183, 195, 0.6);
  border-left: 1px solid rgba(176, 183, 195, 0.6);
}
.card-form__item:nth-child(3) {
  border-right: 1px solid rgba(176, 183, 195, 0.6);
}
.card-form__item:nth-child(4) {
  border: 1px solid rgba(176, 183, 195, 0.6);
  grid-column: 1 /3;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.card-form-card_number-error > .card-form__item:nth-child(1) {
  border-color: red;
  *{
    color: red;
  }
}
.card-form-name-error > .card-form__item:nth-child(4) {
  border-color: red;
}

.card-form-cvv-error > .card-form__item:nth-child(1) {
  border-bottom: none;
}
.card-form-cvv-error > .card-form__item:nth-child(2) {
  border: 1px solid rgba(176, 183, 195, 0.6);
  border-right: none;
}
.card-form-cvv-error > .card-form__item:nth-child(3) {
  border: 1px solid red;
}
.card-form-cvv-error > .card-form__item:nth-child(4) {
  border-top: none;
}

.card-form-exp_date-error > .card-form__item:nth-child(1) {
  border-top: 1px solid rgba(176, 183, 195, 0.6);
  border-bottom: none;
}
.card-form-exp_date-error > .card-form__item:nth-child(2) {
  border: 1px solid red;
}
.card-form-exp_date-error > .card-form__item:nth-child(3) {
  border: 1px solid rgba(176, 183, 195, 0.6);
  border-left: none;
}
.card-form-exp_date-error > .card-form__item:nth-child(4) {
  border-top: none;
}

  .payment{
    &__title{
      font-weight: 600;
      font-size: 14px;
      line-height: 21px;
      color: $primary-gray;
      margin-bottom: 14px;
    }
    &__header{
      margin-bottom: 20px;
    }
    &__picker{
      display: flex;
      justify-content: space-between;
      gap: 16px;
    }
    &__card{
      width: 100%;
      max-width: 168px;
    }
  }
  .payment-card{
    padding: 10px;
    width: 100%;
    border: 1px solid $secondary-gray;
    border-radius: 6px;
    &__text{
      font-weight: 400;
      font-size: 14px;  
      line-height: 16px;
      color: $primary-blue;
    }
    &--active{
      border: 1px solid $primary-blue;
      background-color: rgba(176, 183, 195, 0.2);;
    }
  }
  .receipt{
    &__title{
      font-weight: 400;
      font-size: 14px;
      line-height: 22px;
      color: $primary-blue;
      margin-bottom: 10px;

    }
  }
.js-sdk-error{
  margin-top: 5px;
  color: red;
  font-size: 14px;
}
.tele2 {
  .payment{
    &__title{
      font-family: 'Open sans',sans-serif;
      font-size: 13px;
    }
  }
  .card-form__item:nth-child(1) {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  }
  .card-form__item:nth-child(4) {
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }

}
@media (min-width: 320px) and (max-width: 425px) {
  .payment{
    &__picker{
      flex-direction: column;
    }
    &__card{
      max-width: 100%;
      margin-bottom: 15px;
      &:last-child{
        margin-bottom: 0;
      }
    }
  }
}
</style>
